import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Block, BlockStore } from '@/types';
import api from '@/lib/api';

const DEBOUNCE_MS = 600;
const pendingUpdates = new Map<string, number>();

const MAX_BLOCK_HISTORY = 50;

function cloneBlocks(blocks: Block[]): Block[] {
  return JSON.parse(JSON.stringify(blocks)) as Block[];
}

export const useBlockStore = create<BlockStore>()(
  devtools(
    (set, get) => {
      let structureSyncId = 0;

      /** Markeert één pending persist als afgerond (geslaagd of mislukt). */
      const settlePendingSave = (id: string, failed: boolean, error?: unknown) => {
        set((state) => {
          const failedBlockSaves = failed
            ? state.failedBlockSaves.includes(id)
              ? state.failedBlockSaves
              : [...state.failedBlockSaves, id]
            : state.failedBlockSaves.filter((f) => f !== id);
          return {
            pendingBlockSaves: Math.max(0, state.pendingBlockSaves - 1),
            failedBlockSaves,
            lastBlockSaveError: failed
              ? error
              : failedBlockSaves.length
                ? state.lastBlockSaveError
                : undefined,
          };
        });
      };

      const syncStructureToServer = async (fromBlocks: Block[], toBlocks: Block[]) => {
        const syncId = ++structureSyncId;
        const quoteId = toBlocks[0]?.quoteId || fromBlocks[0]?.quoteId;
        if (!quoteId) return;

        const fromIds = new Set(fromBlocks.map((b) => b.id));
        const toIds = new Set(toBlocks.map((b) => b.id));

        for (const id of fromIds) {
          if (!toIds.has(id)) {
            try {
              await api.deleteBlock(quoteId, id);
            } catch (error) {
              console.error('syncStructureToServer delete failed:', id, error);
            }
          }
        }

        if (syncId !== structureSyncId) return;

        const sortedTo = [...toBlocks].sort((a, b) => a.order - b.order);
        const fromById = new Map(fromBlocks.map((b) => [b.id, b]));
        const nextBlocks: Block[] = [];

        for (let i = 0; i < sortedTo.length; i++) {
          const b = sortedTo[i];
          const before = fromById.get(b.id);
          if (before) {
            nextBlocks.push({ ...b, order: i });
            // Een structurele stap kan ook een bestaande pagina wijzigen (een afbeelding die
            // naar een nieuwe pagina ging); zonder dit staat die inhoud alleen lokaal terug.
            const changed =
              JSON.stringify([before.content, before.styles, before.config]) !==
              JSON.stringify([b.content, b.styles, b.config]);
            if (changed) {
              try {
                await api.updateBlock(quoteId, b.id, {
                  content: b.content,
                  styles: b.styles,
                  config: b.config,
                  type: b.type,
                });
              } catch (error) {
                console.error('syncStructureToServer update failed:', b.id, error);
              }
            }
          } else {
            try {
              const created = await api.createBlock(quoteId, {
                quoteId,
                type: b.type,
                order: i,
                content: b.content,
                styles: b.styles,
                config: b.config,
              });
              nextBlocks.push({ ...created, order: i });
            } catch (error) {
              console.error('syncStructureToServer create failed:', error);
              nextBlocks.push({ ...b, order: i });
            }
          }
        }

        if (syncId !== structureSyncId) return;

        try {
          await api.reorderBlocks(quoteId, nextBlocks.map((b) => b.id));
        } catch (error) {
          console.error('syncStructureToServer reorder failed:', error);
        }

        if (syncId !== structureSyncId) return;

        const normalized = nextBlocks.map((b, i) => ({ ...b, order: i }));
        set({ blocks: normalized });
      };

      return {
        blocks: [],
        blockHistoryPast: [] as Block[][],
        blockHistoryFuture: [] as Block[][],
        pendingBlockSaves: 0,
        failedBlockSaves: [] as string[],
        lastBlockSaveError: undefined,
        externalBlocksRevision: 0,

        setBlocks: (blocks, options?: { preserveStructuralHistory?: boolean }) => {
          if (options?.preserveStructuralHistory) {
            set({ blocks });
          } else {
            set({
              blocks,
              blockHistoryPast: [],
              blockHistoryFuture: [],
            });
          }
        },

        addBlock: async (block: Omit<Block, 'id' | 'createdAt' | 'updatedAt'>) => {
          set((state) => ({
            blockHistoryPast: [...state.blockHistoryPast, cloneBlocks(state.blocks)].slice(-MAX_BLOCK_HISTORY),
            blockHistoryFuture: [],
          }));
          try {
            const newBlock = await api.createBlock(block.quoteId, block);
            set((state) => ({
              blocks: [...state.blocks, newBlock],
            }));
            return newBlock;
          } catch (error) {
            set((state) => ({
              blockHistoryPast: state.blockHistoryPast.slice(0, -1),
            }));
            console.error('Failed to add block:', error);
            throw error;
          }
        },

        updateBlock: async (id: string, data: Partial<Block>, options?: { external?: boolean }) => {
          set((state) => ({
            blocks: state.blocks.map((b) =>
              b.id === id ? { ...b, ...data, content: data.content ?? b.content, styles: data.styles ?? b.styles } : b
            ),
            ...(options?.external
              ? { externalBlocksRevision: state.externalBlocksRevision + 1 }
              : {}),
          }));

          const existing = pendingUpdates.get(id);
          if (existing) {
            window.clearTimeout(existing);
          } else {
            set((state) => ({ pendingBlockSaves: state.pendingBlockSaves + 1 }));
          }

          const timer = window.setTimeout(async () => {
            if (pendingUpdates.get(id) !== timer) return;
            pendingUpdates.delete(id);
            try {
              const currentBlock = get().blocks.find((b) => b.id === id);
              const quoteId = data.quoteId || currentBlock?.quoteId;
              // Intussen verwijderd (bijv. door Ongedaan maken): niets meer op te slaan.
              if (!quoteId || !currentBlock) {
                settlePendingSave(id, false);
                return;
              }

              await api.updateBlock(quoteId, id, {
                content: currentBlock?.content,
                styles: currentBlock?.styles,
                config: currentBlock?.config,
                type: currentBlock?.type,
              });
              settlePendingSave(id, false);
            } catch (error) {
              console.error('Failed to persist block update:', error);
              settlePendingSave(id, true, error);
            }
          }, DEBOUNCE_MS);

          pendingUpdates.set(id, timer);
        },

        flushPendingBlockUpdates: async () => {
          const pendingIds = [...pendingUpdates.keys()];
          for (const id of pendingIds) {
            const t = pendingUpdates.get(id);
            if (t) window.clearTimeout(t);
            pendingUpdates.delete(id);
          }
          // Eerder mislukte persists krijgen bij een expliciete flush een nieuwe poging.
          const retryIds = get().failedBlockSaves.filter((id) => !pendingIds.includes(id));
          if (retryIds.length > 0) {
            set((state) => ({ pendingBlockSaves: state.pendingBlockSaves + retryIds.length }));
          }
          const ids = [...pendingIds, ...retryIds];
          const state = get();
          let allSucceeded = true;
          for (const id of ids) {
            const block = state.blocks.find((b) => b.id === id);
            if (!block?.quoteId) {
              settlePendingSave(id, false);
              continue;
            }
            try {
              await api.updateBlock(block.quoteId, id, {
                content: block.content,
                styles: block.styles,
                config: block.config,
                type: block.type,
              });
              settlePendingSave(id, false);
            } catch (error) {
              console.error('Failed to flush block update:', id, error);
              settlePendingSave(id, true, error);
              allSucceeded = false;
            }
          }
          return allSucceeded;
        },

        deleteBlock: async (id: string) => {
          const pending = pendingUpdates.get(id);
          if (pending) {
            window.clearTimeout(pending);
            pendingUpdates.delete(id);
            settlePendingSave(id, false);
          }

          const block = get().blocks.find((b) => b.id === id);
          if (!block) return;

          set((state) => ({
            blockHistoryPast: [...state.blockHistoryPast, cloneBlocks(state.blocks)].slice(-MAX_BLOCK_HISTORY),
            blockHistoryFuture: [],
            blocks: state.blocks.filter((b) => b.id !== id),
          }));

          try {
            await api.deleteBlock(block.quoteId, id);
          } catch (error) {
            console.error('Failed to delete block:', error);
            set((state) => ({
              blockHistoryPast: state.blockHistoryPast.slice(0, -1),
              blocks: [...state.blocks, block].sort((a, b) => a.order - b.order),
            }));
            throw error;
          }
        },

        reorderBlocks: async (quoteId: string, blockIds: string[]) => {
          const previousBlocks = [...get().blocks];
          set((state) => {
            const reorderedBlocks = blockIds
              .map((id, index) => {
                const b = state.blocks.find((bl) => bl.id === id);
                return b ? { ...b, order: index } : undefined;
              })
              .filter((b): b is Block => b !== undefined);
            return {
              blockHistoryPast: [...state.blockHistoryPast, cloneBlocks(state.blocks)].slice(-MAX_BLOCK_HISTORY),
              blockHistoryFuture: [],
              blocks: reorderedBlocks,
            };
          });

          try {
            await api.reorderBlocks(quoteId, blockIds);
          } catch (error) {
            console.error('Failed to reorder blocks:', error);
            set((state) => ({
              blockHistoryPast: state.blockHistoryPast.slice(0, -1),
              blocks: previousBlocks,
            }));
            throw error;
          }
        },

        pushStructuralSnapshot: () => {
          set((state) => ({
            blockHistoryPast: [...state.blockHistoryPast, cloneBlocks(state.blocks)].slice(-MAX_BLOCK_HISTORY),
            blockHistoryFuture: [],
          }));
        },

        undoBlocks: async () => {
          const past = get().blockHistoryPast;
          if (past.length === 0) return;
          const current = cloneBlocks(get().blocks);
          const snapshot = cloneBlocks(past[past.length - 1]);
          set((state) => ({
            blocks: snapshot,
            blockHistoryPast: past.slice(0, -1),
            blockHistoryFuture: [current, ...state.blockHistoryFuture].slice(0, MAX_BLOCK_HISTORY),
          }));

          try {
            await syncStructureToServer(current, snapshot);
          } catch (error) {
            console.error('undoBlocks sync failed:', error);
          }
        },

        redoBlocks: async () => {
          const future = get().blockHistoryFuture;
          if (future.length === 0) return;
          const current = cloneBlocks(get().blocks);
          const [next, ...restFuture] = future;
          const target = cloneBlocks(next);
          set((state) => ({
            blocks: target,
            blockHistoryFuture: restFuture,
            blockHistoryPast: [...state.blockHistoryPast, current].slice(-MAX_BLOCK_HISTORY),
          }));

          try {
            await syncStructureToServer(current, target);
          } catch (error) {
            console.error('redoBlocks sync failed:', error);
          }
        },
      };
    },
    { name: 'BlockStore' }
  )
);
